/**
 * Briefing Room — pre-sortie screen.
 *
 * Shows mission info, content set stats, and "STEP TO AIRCRAFT" button.
 */

import { useNavigate } from 'react-router-dom'
import type { ContentSet, ContentStats, GameSessionData } from './types'

interface BriefingRoomProps {
  session: GameSessionData | null
  contentSets: ContentSet[]
  selectedSet: ContentSet | null
  stats: ContentStats | null
  isLoading: boolean
  /** Shown above the launch button so a failed launch is never silent. */
  error?: string | null
  onSelectSet: (set: ContentSet) => void
  onLaunch: () => void
}

function getPilotRank(masteryPct: number): { rank: string; flavor: string } {
  if (masteryPct >= 90) return { rank: 'TOP GUN', flavor: 'Master of the skies' }
  if (masteryPct >= 70) return { rank: 'ACE', flavor: 'Elite combat pilot' }
  if (masteryPct >= 50) return { rank: 'FLIGHT LEAD', flavor: 'Leading the formation' }
  if (masteryPct >= 25) return { rank: 'WINGMAN', flavor: 'Building skill fast' }
  return { rank: 'CADET', flavor: 'Training in progress' }
}

export default function BriefingRoom({
  session,
  contentSets,
  selectedSet,
  stats,
  isLoading,
  error,
  onSelectSet,
  onLaunch,
}: BriefingRoomProps) {
  const navigate = useNavigate()

  const mathSets = contentSets.filter(
    (s) => s.category === 'math-facts'
  )

  const pilotRank = stats ? getPilotRank(stats.masteryPercentage) : null

  return (
    <div className="min-h-screen bg-[#020617] text-[#00FF41] font-mono flex flex-col items-center justify-center p-6">
      {/* Scanline overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-50 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(to bottom, transparent, transparent 50%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.3))',
          backgroundSize: '100% 4px',
        }}
      />

      <div className="max-w-lg w-full space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1
            className="text-3xl font-bold tracking-widest"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            MISSION BRIEFING
          </h1>
          <div className="text-[#00FF4166] text-sm tracking-[0.3em]">
            FACT STRIKE OPERATIONS CENTER
          </div>
        </div>

        {/* Pilot info */}
        {session && (
          <div className="border border-[#00FF4140] bg-black/40 p-4 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="opacity-60">CALLSIGN</span>
              <span className="font-bold">{session.player.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="opacity-60">PILOT STATUS</span>
              <span className="font-bold">
                {pilotRank ? pilotRank.rank : session.player.title}
              </span>
            </div>
            {pilotRank && (
              <div className="text-xs text-[#00FF4180] text-right italic">
                {pilotRank.flavor}
              </div>
            )}
            <div className="flex justify-between">
              <span className="opacity-60">LEVEL</span>
              <span>{session.player.level}</span>
            </div>
          </div>
        )}

        {/* Content set selection */}
        <div className="space-y-3">
          <div className="text-xs tracking-widest opacity-60">SELECT OPERATION</div>
          <div className="space-y-2">
            {mathSets.map((set) => {
              const isSelected = selectedSet?.id === set.id
              const operation = set.name.toLowerCase().includes('mult')
                ? 'MULTIPLICATION'
                : set.name.toLowerCase().includes('div')
                  ? 'DIVISION'
                  : set.name.toLowerCase().includes('add')
                    ? 'ADDITION'
                    : set.name.toLowerCase().includes('sub')
                      ? 'SUBTRACTION'
                      : set.name.toUpperCase()

              return (
                <button
                  key={set.id}
                  onClick={() => onSelectSet(set)}
                  className={`w-full text-left p-3 border transition-all ${
                    isSelected
                      ? 'border-[#00FF41] bg-[#00FF41]/10 shadow-[0_0_10px_rgba(0,255,65,0.3)]'
                      : 'border-[#00FF4130] bg-black/30 hover:border-[#00FF4180]'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-sm">{operation}</span>
                    <span className="text-xs opacity-60">{set.itemCount} facts</span>
                  </div>
                </button>
              )
            })}
            {mathSets.length === 0 && !isLoading && (
              <div className="text-center opacity-40 py-4 text-sm">
                No content sets available. Load math facts first.
              </div>
            )}
          </div>
        </div>

        {/* Mastery stats for selected set */}
        {stats && selectedSet && (
          <div className="border border-[#00FF4130] bg-black/30 p-4 space-y-4">
            <div className="text-xs tracking-widest opacity-60">INTEL REPORT</div>

            {/* Mastery progress bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span>MASTERY PROGRESS</span>
                <span className="font-bold">{stats.masteryPercentage}%</span>
              </div>
              <div className="h-3 bg-[#111] border border-[#00FF4120] overflow-hidden flex">
                {stats.masteredCount > 0 && (
                  <div
                    className="h-full bg-[#00FF41]"
                    style={{ width: `${(stats.masteredCount / stats.totalItems) * 100}%` }}
                  />
                )}
                {stats.practicedCount > 0 && (
                  <div
                    className="h-full bg-[#00CCAA]"
                    style={{ width: `${(stats.practicedCount / stats.totalItems) * 100}%` }}
                  />
                )}
                {stats.learningCount > 0 && (
                  <div
                    className="h-full bg-[#FFDD33]"
                    style={{ width: `${(stats.learningCount / stats.totalItems) * 100}%` }}
                  />
                )}
              </div>
              {/* Legend */}
              <div className="flex gap-4 text-[10px] flex-wrap">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-[#00FF41] inline-block" /> MASTERED {stats.masteredCount}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-[#00CCAA] inline-block" /> PRACTICED {stats.practicedCount}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-[#FFDD33] inline-block" /> LEARNING {stats.learningCount}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-[#333] inline-block" /> NEW {stats.newCount}
                </span>
              </div>
            </div>

            {/* Items needing attention */}
            {(stats.dueForReviewCount > 0 || stats.lapsedCount > 0) && (
              <div className="border border-yellow-500/30 bg-yellow-500/5 p-3 space-y-1 text-sm">
                {stats.dueForReviewCount > 0 && (
                  <div className="flex items-center gap-2 text-yellow-400">
                    <span className="text-xs">&#9656;</span>
                    <span>{stats.dueForReviewCount} facts due for review</span>
                  </div>
                )}
                {stats.lapsedCount > 0 && (
                  <div className="flex items-center gap-2 text-amber-500">
                    <span className="text-xs">&#9656;</span>
                    <span>{stats.lapsedCount} facts need refreshing</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Launch mission */}
        <div className="space-y-3">
          <div className="text-xs text-center opacity-40">
            STANDARD SORTIE: 15 CONTACTS
          </div>
          {error && (
            <div
              role="alert"
              className="border border-red-500/50 bg-red-500/10 text-red-300 p-3 text-sm text-center"
            >
              {error}
            </div>
          )}
          <button
            onClick={onLaunch}
            // `session` gating matters: without it the button looked live but
            // the launch handler returned immediately, so the tap did nothing.
            disabled={!selectedSet || !session || isLoading}
            className={`w-full py-4 text-lg font-bold tracking-widest border-2 transition-all ${
              selectedSet && session && !isLoading
                ? 'border-[#00FF41] bg-[#00FF41]/10 hover:bg-[#00FF41]/20 hover:shadow-[0_0_20px_rgba(0,255,65,0.4)] cursor-pointer'
                : 'border-[#00FF4130] bg-black/30 opacity-40 cursor-not-allowed'
            }`}
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            {isLoading || !session ? 'LOADING...' : 'STEP TO AIRCRAFT'}
          </button>
        </div>

        {/* Back to Arcade */}
        <div className="text-center">
          <button
            onClick={() => navigate('/student/arcade')}
            className="text-xs text-[#00FF4160] hover:text-[#00FF41] transition-colors tracking-widest"
          >
            &larr; BACK TO ARCADE
          </button>
        </div>
      </div>
    </div>
  )
}
